import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Step,
  StepButton,
  Stepper,
  Typography,
} from "@mui/material";
import { revalidateLogic } from "@tanstack/react-form";
import { useSelector } from "@tanstack/react-store";
import { shouldBlockBelgiumClientMission } from "@utils/functions/Delivery/mission-block";
import { format, isDate, parseISO } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppForm } from "@/components/Forms";
import { useUserContext } from "@/contexts/UserContext/index.context";
import { UserRole } from "@/types/models/user.type";
import { deliveryFormOpts } from "./form-config";
import { useDeliveryFormDefaults } from "./hooks/useDeliveryFormDefaults";
import { useDeliveryFormWizard } from "./hooks/useDeliveryFormWizard";
import StepAddressVehicle from "./steps/StepAddressVehicle";
import StepClientMandataire from "./steps/StepClientMandataire";
import StepReturnAddressVehicle from "./steps/StepReturnAddressVehicle";
import StepSummary from "./steps/StepSummary";
import type { DeliveryFormProps } from "./types";
import { transformToCreateInput, transformToUpdateInput } from "./utils/transform";

export default function DeliveryFormV3({
  type,
  formId,
  data,
  title,
  initialStep = 0,
  onSubmit,
}: DeliveryFormProps) {
  const { t } = useTranslation();
  const { userState } = useUserContext();
  const isAdmin = userState?.["custom:role"] === UserRole.ADMIN;

  const storageKey = `delivery-form-v2-${formId}`;
  const { initialValues, clearStorage, saveToStorage } = useDeliveryFormDefaults(data, storageKey);

  const isEditing = Boolean(data?.id);
  const disablePastDateSelection = !(isEditing && isAdmin);

  const { step, setStep, nextStep, prevStep, isTwoWay, isLastStep, steps, getStepTitle } =
    useDeliveryFormWizard(type, initialStep);

  const form = useAppForm({
    ...deliveryFormOpts,
    defaultValues: initialValues,
    validationLogic: revalidateLogic(),
    onSubmit: async ({ value }) => {
      clearStorage();
      onSubmit(value);
    },
  });

  const values = useSelector(form.store, (state) => state.values);

  const [blockedMissionModalOpen, setBlockedMissionModalOpen] = useState(false);
  const [blockedDateDisplay, setBlockedDateDisplay] = useState("");

  const handleStepValidated = () => {
    // Blocked mission check - after form validation passes
    if (!isAdmin && step === 1) {
      const countries = [
        values.step1?.fromAddress?.country,
        values.step1?.toAddress?.country,
        values.step2?.toReturnFromAddress?.country,
        values.step2?.toReturnToAddress?.country,
      ];

      const shouldBlock = shouldBlockBelgiumClientMission({
        isAdmin,
        step,
        deadline: values.step0?.carDeliveryDeadline,
        countries,
        blockedDatesRaw: import.meta.env.VITE_BLOCKED_MISSION_DATES as string | undefined,
      });

      if (shouldBlock) {
        console.log("🚫 Mission blocked!");
        const deadline = values.step0?.carDeliveryDeadline;
        const date = typeof deadline === "string" ? parseISO(deadline) : deadline;
        const formattedDate = date && isDate(date) ? format(date, "d MMMM yyyy") : "";
        setBlockedDateDisplay(formattedDate);
        setBlockedMissionModalOpen(true);
        return;
      }
    }

    const isCreateStep = (step === 1 && !isTwoWay) || (step === 2 && isTwoWay);

    // If moving from Step 1 to Step 2 for a swap, hard-copy the values into the store
    if (isTwoWay && step === 1) {
      const currentValues = form.store.state.values;
      const toAddress = currentValues.step1.toAddress;

      form.setFieldValue("step2.toReturnFromAddress.address", toAddress.address || "");
      form.setFieldValue("step2.toReturnFromAddress.city", toAddress.city || "");
      form.setFieldValue("step2.toReturnFromAddress.postalCode", toAddress.postalCode || "");
      form.setFieldValue("step2.toReturnFromAddress.country", toAddress.country || "");
      form.setFieldValue("step2.toReturnFromAddress.latitude", toAddress.latitude || 0);
      form.setFieldValue("step2.toReturnFromAddress.longitude", toAddress.longitude || 0);
      form.setFieldValue("step2.toReturnFromAddress.place_id", toAddress.place_id || "");
      form.setFieldValue(
        "step2.toReturnFromAddress.addressPrecision",
        toAddress.addressPrecision || ""
      );
    }

    if (isCreateStep && !data?.id) {
      // Clear draft so the next "new delivery" starts empty
      clearStorage();
      onSubmit(form.store.state.values);
      return;
    }

    // Persist draft while moving between steps on create
    if (!data?.id) {
      saveToStorage(form.store.state.values);
    }
    nextStep();
  };

  const handleLastStepValidated = () => {
    const currentValues = form.store.state.values;
    if (data?.id) {
      const payload = transformToUpdateInput(currentValues, data.id, type);
      onSubmit(payload);
    } else {
      const payload = transformToCreateInput(currentValues, type);
      onSubmit(payload);
    }
    clearStorage();
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  const renderStepContent = () => {
    if (step === 0) {
      return (
        <StepClientMandataire
          form={form}
          isAdmin={isAdmin}
          onStepValid={handleStepValidated}
          disablePast={disablePastDateSelection}
        />
      );
    }
    if (step === 1) {
      return (
        <StepAddressVehicle
          form={form}
          type={type}
          onStepValid={handleStepValidated}
          onPrevious={prevStep}
        />
      );
    }
    if (isTwoWay && step === 2) {
      return (
        <StepReturnAddressVehicle
          form={form}
          onStepValid={handleStepValidated}
          onPrevious={prevStep}
        />
      );
    }
    if (isLastStep) {
      return (
        <StepSummary
          form={form}
          data={data}
          type={type}
          isAdmin={isAdmin}
          onStepValid={handleLastStepValidated}
          onPrevious={prevStep}
        />
      );
    }
    return null;
  };

  return (
    <>
      <Container maxWidth="xl">
        <Box>
          <Grid container padding={4} spacing={2}>
            {/* Title */}
            <Grid size={{ xs: 12 }}>
              <Typography
                fontWeight={600}
                textAlign="center"
                textTransform="capitalize"
                variant="h4"
              >
                {isLastStep ? "Validation" : title}
              </Typography>
            </Grid>

            {/* Step subtitle */}
            <Container
              component="main"
              disableGutters
              maxWidth="md"
              sx={{ pb: 4, textAlign: "center", color: "GrayText" }}
            >
              <Typography variant="body1">{getStepTitle(step)}</Typography>
            </Container>

            {/* Stepper */}
            <Grid size={{ xs: 12 }}>
              <Stepper activeStep={step} sx={{ mb: 8 }}>
                {steps.map((_, i) => (
                  <Step key={i} sx={stepperSx}>
                    <StepButton color="inherit" disabled={i > step} onClick={() => setStep(i)} />
                  </Step>
                ))}
              </Stepper>
            </Grid>

            {/* Step content (wrapped in form.AppForm for context) */}
            <Grid size={{ xs: 12 }}>
              <form.AppForm>{renderStepContent()}</form.AppForm>
            </Grid>
          </Grid>
        </Box>
      </Container>
      {blockedMissionModalOpen && (
        <Dialog
          open={true}
          onClose={() => setBlockedMissionModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("delivery.validation-modal.title")}</DialogTitle>
          <DialogContent>
            <Typography sx={{ whiteSpace: "pre-line" }}>
              {t("delivery.validation-modal.descriptions.blocked-mission-date", {
                date: blockedDateDisplay,
              })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBlockedMissionModalOpen(false)}>
              {t("common.actions.close", "Close")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

const stepperSx = {
  "& .MuiStepLabel-label": {
    color: "white",
    xs: { fontSize: "0.7rem", fontWeight: 600, textTransform: "capitalize", m: 1 },
    md: { fontSize: "1rem" },
  },
  "& .MuiStepLabel-iconContainer svg": {
    xs: { width: "1.5rem", height: "1.5rem" },
    md: { width: "2rem", height: "2rem" },
  },
};
